/**
 * A read, as three cases instead of four booleans.
 *
 * React Query hands back `isPending`, `isError`, `error` and a `data` that is
 * `T | undefined` in every one of those states. Nothing in the type stops a
 * component reading `data` on the error branch, so every consumer ends up
 * writing the same `query.isError || !query.data` guard - and the one that
 * forgets the `!query.data` half renders `undefined` rows.
 *
 * `Async<T>` is that guard written once. Past `fromQuery` there is exactly one
 * branch that carries a value, so there is no undefined to forget.
 */
export type Async<T> =
  | { kind: "loading" }
  | { kind: "failed"; error: unknown }
  | { kind: "loaded"; value: T };

/** The shape of the react-query result this reads - kept structural on purpose. */
export interface QueryLike<T> {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: T | undefined;
}

/**
 * `isPending` first, then `isError`, then the value.
 *
 * A refetch keeps the previous value rather than dropping back to `loading`:
 * `isPending` is only true when there is nothing cached, so a background refresh
 * leaves the table on screen instead of flashing a loader over data that is
 * about to be replaced by nearly the same data.
 */
export function fromQuery<T>(query: QueryLike<T>): Async<T> {
  if (query.isPending) {
    return { kind: "loading" };
  }

  if (query.isError || query.data === undefined) {
    return { kind: "failed", error: query.error };
  }

  return { kind: "loaded", value: query.data };
}

/** Map the loaded case, leaving the other two alone. */
export function mapAsync<T, U>(
  async: Async<T>,
  project: (value: T) => U,
): Async<U> {
  return async.kind === "loaded"
    ? { kind: "loaded", value: project(async.value) }
    : async;
}
